import { notifyOnError } from '~/rage/decorators';
import { IModalState, ModalType } from '~/shared';
import { BackendService } from '../BackendService';
import { IGuiService } from '../GuiService';
import { INotificationsService } from '../NotificationsService';
import { AbstractModalService } from './AbstractModalService';
import { IModalService } from './modalServiceTypes';

const replyDecline = async (
	backend: BackendService,
	id: string,
	notifications: INotificationsService,
) => {
	try {
		await backend.invoke('modal:reply', {
			id,
			result: { isAccepted: false },
		});
	} catch (error) {
		notifications.showException(error);
	}
};

export class ModalService
	extends AbstractModalService
	implements IModalService
{
	private readonly displayedModals = new Set<string>();

	constructor(
		private readonly backend: BackendService,
		private readonly notifications: INotificationsService,
		private readonly gui: IGuiService,
	) {
		super();

		this.backend.addEventHandler(
			'modals:show',
			this.handleModalsShow.bind(this),
		);

		this.getCurrentModal().subscribe((modal) => {
			this.gui.setCursorVisible(
				'modal',
				modal != null && modal.type !== ModalType.Invite,
			);
		});
	}

	@notifyOnError
	private async handleModalsShow(data: { id: string; state: IModalState }) {
		if (this.displayedModals.has(data.id)) {
			return;
		}

		const isInvite = data.state.type === ModalType.Invite;
		if (isInvite) {
			const shouldDeclineInvite = await this.gui.shouldDeclineInvite();

			if (shouldDeclineInvite) {
				await replyDecline(this.backend, data.id, this.notifications);
				return;
			}
		}

		this.displayedModals.add(data.id);

		this.showModal(data.state, async (result) => {
			try {
				await this.backend.invoke('modal:reply', {
					id: data.id,
					result,
				});

				this.displayedModals.delete(data.id);
			} catch (error) {
				this.notifications.showException(error);
			}
		});
	}
}
